import { chains } from "@/src/evm/chains/index.js";
import { Protocol } from "@/src/evm/enums.js";
import { resolvers } from "@/src/evm/releases/resolvers.js";
import type { Sablier } from "@/src/types.js";
import aliases from "./aliases.js";
import manifest from "./manifest.js";

function get(chainId: number, contractMap: Sablier.EVM.ContractMap): Sablier.EVM.Deployment {
  return resolvers.deployment.standard({
    aliasMap: aliases,
    chainId,
    contractMap,
    protocol: Protocol.Lockup,
    version: "v3.0",
  });
}

/**
 * @description Mainnet deployments for Lockup v3.0
 */
export const mainnets: Sablier.EVM.Deployment[] = [
  get(chains.abstract.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x63Ff2E370788C163D5a1909B5FCb299DB327AEF9",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x47a27E76b32A9aA168297A32c5F081740600A16F",
    [manifest.SABLIER_LOCKUP]: ["0x293d8d192C0C93225FF6bBE7415a56B57379bbA3", 20_582_992],
  }),
  get(chains.arbitrum.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xd5c6a0Dd2E1822865c308850b8b3E2CcE762D061",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xf094baa1b754f54d8f282bc79a74bd76aff29d25",
    [manifest.SABLIER_LOCKUP]: ["0xF12AbfB041b5064b839Ca56638cDB62fEA712Db5", 385_670_671],
  }),
  get(chains.avalanche.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x906A4BD5dD0EF13654eA29bFD6185d0d64A4b674",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x7125669bFbCA422bE806d62B6b21E42ED0D78494",
    [manifest.SABLIER_LOCKUP]: ["0x7e146250Ed5CCCC6Ada924D456947556902acaFD", 69_716_263],
  }),
  get(chains.base.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x87e437030b7439150605a641483de98672E26317",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x8882549b29dfed283738918d90b5f6e2ab0baeb6",
    [manifest.SABLIER_LOCKUP]: ["0xe261b366f231b12fcb58d6bbd71e57faee82431d", 36_357_687],
  }),
  get(chains.berachain.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x3bbE0a21792564604B0fDc00019532Adeffa70eb",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x35860B173573CbDB7a14dE5F9fBB7489c57a5727",
    [manifest.SABLIER_LOCKUP]: ["0xC37B51a3c3Be55f0B34Fbd8Bd1F30cFF6d251408", 11_304_305],
  }),
  get(chains.blast.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x959c412d5919b1Ec5D07bee3443ea68c91d57dd7",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xd2187e309a93895fcd1c43fb5eae903b583d7e38",
    [manifest.SABLIER_LOCKUP]: ["0xcD16d89cc79Ab0b52717A46b8A3F73E61014c7dc", 25_347_901],
  }),
  get(chains.bsc.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x56831a5a932793E02251126831174Ab8Bf2f7695",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xFEd01907959CD5d470F438daad232a99cAffe67f",
    [manifest.SABLIER_LOCKUP]: ["0x06bd1Ec1d80acc45ba332f79B08d2d9e24240C74", 63_357_633],
  }),
  get(chains.chiliz.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x8A96f827082FB349B6e268baa0a7A5584c4Ccda6",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x202628bCF205BC57a2D181D6B4eF1f7E5538EC35",
    [manifest.SABLIER_LOCKUP]: ["0x957a54aC691893B20c705e0b2EecbDDF5220d019", 27_591_344],
  }),
  get(chains.coreDao.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xac0cf0f2a96ed7ec3cfa4d0be621c67adc9dd903",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x80054b6797C5F657CaCaEC05E4890acfC4Ef79dB",
    [manifest.SABLIER_LOCKUP]: ["0x01Fed2aB51A830a3AF3AE1AB817dF1bA4F152bB0", 28_737_609],
  }),
  // The actual deployment block is 697808, but the Denergy chain does not work before block 710000.
  get(chains.denergy.id, {
    [manifest.HELPERS]: "0xd595D34ed96b253E7c7a934a7624F330a8411953",
    [manifest.LOCKUP_MATH]: "0xB8aB2E66d5E4931e675CBE499d8c01B778694fd3",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x4749dB4834be9b473D586Ad4d98133dAfC678313",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x6Fe81F4Bf1aF1b829f0E701647808f3Aa4b0BdE7",
    [manifest.SABLIER_LOCKUP]: ["0x9f5d28C8ed7F09e65519C1f6f394e523524cA38F", 710_000],
  }),
  get(chains.gnosis.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x3140a6900AA2FF3186730741ad8255ee4e6d8Ff1",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xb778B396dD6f3a770C4B4AE7b0983345b231C16C",
    [manifest.SABLIER_LOCKUP]: ["0x87f87Eb0b59421D1b2Df7301037e923932176681", 42_440_688],
  }),
  get(chains.hyperevm.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x7263d77e9e872f82A15e5E1a9816440D23758708",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xF66b45FA5Be1F633Cd521d480A1832645DE0C710",
    [manifest.SABLIER_LOCKUP]: ["0x50ff828e66612A4D1F7141936F2B4078C7356329", 15_758_852],
  }),
  get(chains.lightlink.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xCFB5F90370A7884DEc59C55533782B45FA24f4d1",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xc1d996e119f82D3Cb540687463804545A13065db",
    [manifest.SABLIER_LOCKUP]: ["0xA4f1f4a5C55b5d9372CBB29112b14e1912A23d9D", 168_180_096],
  }),
  get(chains.linea.id, {
    [manifest.HELPERS]: "0x1b06a7aed1f6e5d8c0f4b410687b37c867fa8987",
    [manifest.LOCKUP_MATH]: "0x8e6f2e21df39ffb23fbd5a51a5903952334435e3",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x1514a869D29a8B22961e8F9eBa3DC64000b96BCe",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x145Ca6411bD62172fbE8728893E8e8E75a2b066c",
    [manifest.SABLIER_LOCKUP]: ["0xc853DB30a908dC1b655bbd4A8B9d5DB8588C13c8", 24_117_277],
  }),
  get(chains.mainnet.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xA9dC6878C979B5cc1d98a1803F0664ad725A1f56",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x0636d83b184d65c242c43de6aad10535bfb9d45a",
    [manifest.SABLIER_LOCKUP]: ["0xcF8ce57fa442ba50aCbC57147a62aD03873FfA73", 23_500_619],
  }),
  get(chains.mode.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x64e7879558b6dfE2f510bd4b9Ad196ef0371EAA8",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x9ba4cE334706F822cbCaa4F6fA650e0054D5fa99",
    [manifest.SABLIER_LOCKUP]: ["0x9513CE572D4f4AAc1Dd493bcd50866235D1c698d", 29_677_617],
  }),
  get(chains.monad.id, {
    [manifest.HELPERS]: "0xa0A1aC47260B95D334763473B868117EF7343aA0",
    [manifest.LOCKUP_MATH]: "0x1feB172238638897B13b69C65feB508a0a96b35D",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x619E7f9832522EDeBd883482Cd3d84653A050725",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x4FCACf614E456728CaEa87f475bd78EC3550E20B",
    [manifest.SABLIER_LOCKUP]: ["0x003F5393F4836f710d492AD98D89F5BFCCF1C962", 34_585_784],
  }),
  get(chains.morph.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x660314f09ac3B65E216B6De288aAdc2599AF14e2",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xf81d757E270790c03E3cd2dbFBc62d38ad2e17f9",
    [manifest.SABLIER_LOCKUP]: ["0xE646D9A037c6B62e4d417592A10f57e77f007a27", 17_491_200],
  }),
  get(chains.optimism.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x41dBa1AfBB6DF91b3330dc009842327A9858Cbae",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xf3aBc38b5e0f372716F9bc00fC9994cbd5A8e6FC",
    [manifest.SABLIER_LOCKUP]: ["0xe2620fB20fC9De61CD207d921691F4eE9d0fffd0", 141_962_546],
  }),
  get(chains.polygon.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xf5e12d0bA25FCa0D738Ec57f149736B2e4C46980",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x3395Db92edb3a992E4F0eC1dA203C92D5075b845",
    [manifest.SABLIER_LOCKUP]: ["0x1E901b0E05A78C011D6D4cfFdBdb28a42A1c32EF", 77_222_315],
  }),
  get(chains.scroll.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x00Ff6443E902874924dd217c1435e3be04f57431",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xa57C667E78BA165e8f09899fdE4e8C974C2dD000",
    [manifest.SABLIER_LOCKUP]: ["0xcb60a39942CD5D1c2a1C8aBBEd99C43A73dF3f8d", 22_483_294],
  }),
  get(chains.sei.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xeaFB40669fe3523b073904De76410b46e79a56D7",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xB33f16e55Ac8Ff6CCdfe466C0D9A25c706952EF8",
    [manifest.SABLIER_LOCKUP]: ["0x1d96e9d05f6910d22876177299261290537cfBBc", 171_487_252],
  }),
  get(chains.sonic.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x955dC7A2170782344FA9Ac11De0C0C42C05De2Fc",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x84A865542640B24301F1C8A8C60Eb098a7e1df9b",
    [manifest.SABLIER_LOCKUP]: ["0x763Cfb7DF1D1BFe50e35E295688b3Df789D2feBB", 49_254_811],
  }),
  get(chains.superseed.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xa4576b58Ec760A8282D081dc94F3dc716DFc61e9",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x48c45c1DF54bDA1F1819AD07B6B4f8B2dcF85BAA",
    [manifest.SABLIER_LOCKUP]: ["0x2F1c6AD6306Bd0200D55b59AD54d4b44067D00E6", 16_675_054],
  }),
  get(chains.unichain.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xa5F12D63E18a28C9BE27B6f3d91ce693320067ba",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xA8B4355D37C3660aE0C8fD14c39D44Ad5EA60b3e",
    [manifest.SABLIER_LOCKUP]: ["0xfFb540fC132dCefb0Fdef96ef63FE2f2F1BD7CFd", 28_781_988],
  }),
  get(chains.xdc.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x4c1311a9d88BFb7023148aB04F7321C2E91c29bf",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xCf2491021E56E04e5ceF582c928d64b185843124",
    [manifest.SABLIER_LOCKUP]: ["0x2266901B1EcF499b4c91B6cBeA8e06700cFbde1e", 94_381_434],
  }),
  get(chains.zksync.id, {
    [manifest.HELPERS]: "0xa0a1ac47260b95d334763473b868117ef7343aa0",
    [manifest.LOCKUP_MATH]: "0x1feb172238638897b13b69c65feb508a0a96b35d",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x955dC7A2170782344FA9Ac11De0C0C42C05De2Fc",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x60dD755dB423EB27983B7000b81e434249670608",
    [manifest.SABLIER_LOCKUP]: ["0xC07E338Ce1aEd183A8b3c55f980548f5E463b5c5", 65_048_306],
  }),
];

/**
 * @description Testnet deployments for Lockup v3.0
 */
export const testnets: Sablier.EVM.Deployment[] = [
  get(chains.arbitrumSepolia.id, {
    [manifest.HELPERS]: "0xa0A1aC47260B95D334763473B868117EF7343aA0",
    [manifest.LOCKUP_MATH]: "0x1feB172238638897B13b69C65feB508a0a96b35D",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x8224eb5D7d76B2D7Df43b868D875E79B11500eA8",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x1e963e9214679757ecbf2fa98499f1e61c44697c",
    [manifest.SABLIER_LOCKUP]: ["0x5bd5a50100d0cbc93837a1d10c816614008554fe", 200_784_408],
  }),
  get(chains.baseSepolia.id, {
    [manifest.HELPERS]: "0xa0A1aC47260B95D334763473B868117EF7343aA0",
    [manifest.LOCKUP_MATH]: "0x1feB172238638897B13b69C65feB508a0a96b35D",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xCA2593027BA24856c292Fdcb5F987E0c25e755a4",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xB88458246a0657a54cc7b32C3f3a19969cdD571A",
    [manifest.SABLIER_LOCKUP]: ["0x5C51EA827Bfa65f7c9AF699e19Ec9fB12A2D40E2", 31_869_450],
  }),

  get(chains.optimismSepolia.id, {
    [manifest.HELPERS]: "0xa0A1aC47260B95D334763473B868117EF7343aA0",
    [manifest.LOCKUP_MATH]: "0x1feB172238638897B13b69C65feB508a0a96b35D",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xDf6163ddD3Ebcb552Cc1379a9c65AFe68683534e",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x279593Bb164c79Ae8220A48F616434203B171E6d",
    [manifest.SABLIER_LOCKUP]: ["0xc9a70D7190bE5dafDce4B09c4e78bB7C4A71255D", 33_852_731],
  }),
  get(chains.sepolia.id, {
    [manifest.HELPERS]: "0xa0A1aC47260B95D334763473B868117EF7343aA0",
    [manifest.LOCKUP_MATH]: "0x1feB172238638897B13b69C65feB508a0a96b35D",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x955dC7A2170782344FA9Ac11De0C0C42C05De2Fc",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x44Fd5d5854833975E5Fc80666a10cF3376C088E0",
    [manifest.SABLIER_LOCKUP]: ["0x6b0307b4338f2963A62106028E3B074C2c0510DA", 9_336_998],
  }),
];
